import { isNil } from 'lodash';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { isReportState, isReturnsBasedBlockState, type StudioRequestSubject, subjectAsOfDate } from 'venn-state';
import {
  FootnoteSymbols,
  getComparisonLabelForBlockLegend,
  getStrategyNameForFilteredSubject,
  getSubjectFromRequestSubject,
  hasAllFeesExcluded,
  hasFeesExcluded,
  isHistoricalSubject,
  Numbers,
} from 'venn-utils';
import { formatDateByFrequency } from '../manage-data/utils';
import { useBlockId } from '../studio-blocks/contexts/BlockIdContext';
import { useBlockSubjectSuperscripts } from '../blocks/components/core/block-subject-footnotes-and-superscripts';
import { useGetRequestSubjectFee } from '../blocks/utils/hooks/use-advisory-fee';

export const NameWithOptionalFee = ({
  subject,
  isHeader,
  isBenchmark,
}: {
  subject: StudioRequestSubject;
  isHeader?: boolean;
  isBenchmark: boolean;
}) => {
  const blockId = useBlockId();

  const name = getComparisonLabelForBlockLegend(subject);
  const strategyName = getStrategyNameForFilteredSubject(subject);
  const isStudio = !useRecoilValue(isReportState);
  const isReturnsBasedBlock = useRecoilValue(isReturnsBasedBlockState(blockId));
  const historicalAsOfDate = useRecoilValue(subjectAsOfDate([blockId, getSubjectFromRequestSubject(subject)]));
  const getRequestSubjectFee = useGetRequestSubjectFee();

  const hasHistoricalAsOfDate = isHistoricalSubject(subject) && !isNil(historicalAsOfDate);
  const showHistoricalAsOfDate = isStudio && isHeader && hasHistoricalAsOfDate;
  const subjectHasAllFeesExcluded = hasAllFeesExcluded(subject);

  const fee =
    subjectHasAllFeesExcluded || !isReturnsBasedBlock ? undefined : getRequestSubjectFee({ subject, isBenchmark });

  const superscripts = useBlockSubjectSuperscripts({ subject, isBenchmark });

  return (
    <PureNameWithOptionalFee
      name={name}
      historicalAsOfDate={showHistoricalAsOfDate ? historicalAsOfDate : undefined}
      fee={fee}
      superscripts={superscripts}
      isStudio={isStudio}
      strategyName={strategyName}
    />
  );
};

export const PureNameWithOptionalFee = ({
  name,
  historicalAsOfDate,
  superscripts,
  isStudio,
  fee,
  strategyName,
}: {
  name: string;
  historicalAsOfDate?: number;
  superscripts?: string;
  isStudio?: boolean;
  fee: number | undefined;
  strategyName?: string;
}) => (
  <span>
    <span>
      {name.trim()}
      {superscripts && <sup>{superscripts}</sup>}
    </span>
    {strategyName && <span className="font-normal block"> ({strategyName})</span>}
    {historicalAsOfDate && isStudio && (
      <span className="font-normal text-venn-grey-80">
        {' '}
        (as of {formatDateByFrequency(historicalAsOfDate, 'DAILY')})
      </span>
    )}
    {fee !== undefined && (
      <span className="italic font-normal max-w-[80px]">({Numbers.safeFormatPercentage(fee)} Fee)</span>
    )}
  </span>
);

export const formatSubjectWithProvidedFee = (
  subject: StudioRequestSubject,
  proxySymbol: string,
  hasProxy: boolean,
  superscripts: string,
  fee: number | undefined,
) =>
  formatNameWithOptionalFee(
    getComparisonLabelForBlockLegend(subject),
    proxySymbol,
    superscripts,
    fee,
    hasProxy,
    getStrategyNameForFilteredSubject(subject),
  );

export const formatSubjectWithOptionalFee = (
  subject: StudioRequestSubject,
  proxySymbol: string,
  hasProxy: boolean,
  superscripts: string,
  fee: number | undefined,
) =>
  formatNameWithOptionalFee(
    getComparisonLabelForBlockLegend(subject),
    proxySymbol,
    superscripts,
    fee,
    hasProxy,
    getStrategyNameForFilteredSubject(subject),
  );

export const formatNameWithOptionalFee = (
  name: string,
  proxySymbol: string,
  superscripts: string,
  fee?: number,
  hasProxy?: boolean,
  strategyName?: string,
): string =>
  `${name}${hasProxy && !superscripts ? `<sup>${proxySymbol}</sup>` : ''}${superscripts ? `<sup>${superscripts}</sup>` : ''}${strategyName ? ` <span class="font-normal block"> (${strategyName})</span>` : ''}${fee !== undefined ? ` <span class="font-normal italic">(${Numbers.safeFormatPercentage(fee)} Fee)</span>` : ''}`;

export const formatExportableSubjectWithOptionalFee = (
  subject: StudioRequestSubject,
  hasProxy = false,
  fee: number | undefined,
) => {
  return formatExportableNameWithOptionalFee(
    getComparisonLabelForBlockLegend(subject),
    fee,
    hasFeesExcluded(subject),
    hasProxy,
    undefined,
  );
};

export const formatExportableSubjectWithExplicitFee = (
  subject: StudioRequestSubject,
  hasProxy = false,
  fee: number | undefined,
) => {
  return formatExportableNameWithOptionalFee(
    getComparisonLabelForBlockLegend(subject),
    fee,
    hasFeesExcluded(subject),
    hasProxy,
    undefined,
  );
};

export const formatExportableNameWithOptionalFee = (
  name: string,
  fee?: number,
  feesExcluded?: boolean,
  hasProxy?: boolean,
  parentName?: string,
): string => {
  const proxyInfo = hasProxy ? FootnoteSymbols.proxyUsage : '';
  const feeInfo =
    fee !== undefined
      ? ` (${Numbers.safeFormatPercentage(fee)} Fee)${feesExcluded ? FootnoteSymbols.excludedInvestments : ''}`
      : '';
  const filteredInfo = parentName ? ` (${name})` : '';
  return `${parentName ?? name}${proxyInfo}${filteredInfo}${feeInfo}`;
};

export default NameWithOptionalFee;
